import type {ReactElement, HTMLAttributes} from 'react'
import {cloneElement, useRef, useState} from 'react'
import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react'

// defined here so we don't have dependencies
export type Placement = 'top' | 'bottom' | 'left' | 'right'
export type OverlayType = 'hover' | 'click' | 'focus'
export type OverlayTriggerProps = {
  children: ReactElement<HTMLAttributes<HTMLElement> & {ref?: unknown}>
  trigger?: OverlayType | OverlayType[]
  overlay: ReactElement
  placement?: Placement
  defaultShow?: boolean
  onOpenChanged?: (open: boolean) => void
  delay?: {open: number; close: number}
}
const ARROw_HEIGHT = 7
const GAP = 2
export function OverlayTrigger(props: OverlayTriggerProps): ReactElement {
  const [isOpen, setOpen] = useState(props.defaultShow ?? false)
  const arrowRef = useRef(null)

  const {refs, floatingStyles, context} = useFloating({
    whileElementsMounted: autoUpdate,
    middleware: [offset(ARROw_HEIGHT + GAP), shift(), arrow({element: arrowRef})],
    placement: props.placement,
    open: isOpen,
    onOpenChange: open => {
      setOpen(open)
      if (props.onOpenChanged !== undefined) {
        props.onOpenChanged(open)
      }
    }
  })
  const trigger = props.trigger ?? []
  const triggers: OverlayType[] = Array.isArray(trigger) ? trigger : [trigger]
  const role = useRole(context)
  const hover = useHover(context, {
    handleClose: safePolygon(),
    delay: props.delay,
    enabled: triggers.includes('hover')
  })
  const focus = useFocus(context, {enabled: triggers.includes('focus')})
  const click = useClick(context, {enabled: triggers.includes('click')})
  const dismiss = useDismiss(context, {enabled: triggers.includes('click')})
  const selectedInteractions = [role, dismiss, hover, focus, click]
  const {getReferenceProps, getFloatingProps} = useInteractions(selectedInteractions)
  // eslint-disable-next-line
  if (props.overlay === null) {
    // in tests sometimes this ends up with null, it's never directly called that way, so I would assume no behavior is expected
    return <></>
  }
  const overlay = (
    <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()} key="1">
      {/* eslint-disable-next-line i18next/no-literal-string */}
      <FloatingArrow ref={arrowRef} context={context} fill="white" />
      {props.overlay}
    </div>
  )
  const child = cloneElement(props.children, {ref: refs.setReference, ...getReferenceProps()})
  return (
    <>
      {child}
      {isOpen ? <FloatingPortal>{overlay}</FloatingPortal> : null}
    </>
  )
}
